<template>
  <div class="bg-white rounded-lg border border-gray-200 mb-12">
    <ComponentHeader title="Role Configurations" header-class="sticky top-0 z-10 bg-white rounded-t-lg">
        <div class="flex items-center gap-2">
          <input
            v-model="featureSearch"
            type="text"
            class="px-3 py-1.5 text-sm border border-gray-300 rounded-md w-48 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
            placeholder="Search features..."
          />
          <ExportMenu :disabled="!displayRoles.length" :exports="[{ type: 'excel', action: exportToExcel }]" />
          <button
            v-if="canAddFeature && addableFeatures.length > 0"
            class="btn-secondary-sm"
            @click="openAddFeatureModal"
          >
            <font-awesome-icon :icon="['fas', 'plus']" /> Add Feature
          </button>
          <button
            v-if="canCreateRole"
            :class="{ 'btn-primary-sm': !isDirty, 'btn-secondary-sm': isDirty }"
            @click="openCreateRoleModal"
          >
            <font-awesome-icon :icon="['fas', 'plus']" /> Create Role
          </button>
          <button
            v-if="isDirty"
            class="btn-secondary-sm"
            @click="discardChanges"
          >
            <font-awesome-icon :icon="['fas', 'times']" />
            Discard
          </button>
          <button
            v-if="isDirty && canEditRoles"
            class="btn-primary-sm"
            :disabled="saving"
            @click="saveChanges"
          >
            <font-awesome-icon :icon="['fas', 'floppy-disk']" /> {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
    </ComponentHeader>
    <div class="overflow-x-auto">
      <table class="min-w-full divide-y divide-gray-200">
        <template v-if="isSuperuser">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                API Access Levels
              </th>
              <th
                v-for="role in displayRoles"
                :key="role.role_id"
                :class="[
                  'px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider',
                  canEditRoles ? 'cursor-pointer hover:bg-gray-100 group' : '',
                ]"
                @click="canEditRoles ? openEditRoleModal(role) : null"
              >
                <div class="flex items-center justify-center gap-1">
                  <span class="leading-tight">{{ role.display_name }}</span>
                  <font-awesome-icon
                    v-if="canEditRoles"
                    :icon="['fas', 'pencil']"
                    class="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white">
            <tr style="vertical-align: middle;">
              <td class="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                Access Levels
              </td>
              <td
                v-for="role in displayRoles"
                :key="role.role_id"
                class="px-4 py-2 text-center align-middle"
              >
                <div class="flex flex-wrap gap-1 justify-center">
                  <AccessLevelBadge
                    v-for="level in (roleAccessLevels[role.role_id] || [])"
                    :key="level"
                    :level="level"
                    :label="accessLevelDisplay(level)"
                  />
                  <span
                    v-if="!(roleAccessLevels[role.role_id] || []).length"
                    class="text-xs text-gray-400 italic"
                  >None</span>
                </div>
              </td>
            </tr>
          </tbody>
        </template>
        <template v-for="featureGroup in groupedFeatures" :key="featureGroup.category">
          <thead class="bg-gray-50">
            <tr>
              <th class="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">
                {{ featureGroup.category_name }}
              </th>
              <th
                v-for="role in displayRoles"
                :key="role.role_id"
                :class="[
                  'px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider',
                  canEditRoles ? 'cursor-pointer hover:bg-gray-100 group' : '',
                ]"
                @click="canEditRoles ? openEditRoleModal(role) : null"
              >
                <div class="flex items-center justify-center gap-1">
                  <span class="leading-tight">{{ role.display_name }}</span>
                  <font-awesome-icon
                    v-if="canEditRoles"
                    :icon="['fas', 'pencil']"
                    class="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="feature in featureGroup.features" :key="feature.id">
              <tr style="vertical-align: middle;">
                <td
                  :class="['px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900', feature.children && feature.children.length ? 'cursor-pointer select-none' : '']"
                  :title="featureTooltip(feature)"
                  @click="feature.children && feature.children.length ? toggleExpanded(feature.id) : null"
                >
                  <font-awesome-icon
                    v-if="feature.children && feature.children.length"
                    :icon="['fas', expandedFeatures[feature.id] ? 'chevron-down' : 'chevron-right']"
                    class="w-3 h-3 text-gray-400 mr-1"
                  />
                  {{ feature.name }}
                  <span
                    v-if="feature.is_beta"
                    class="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full border border-purple-400 text-purple-600 normal-case tracking-normal leading-none inline-block"
                    title="Beta feature — hidden from non-superuser views"
                  >Beta</span>
                  <AccessLevelBadge
                    v-if="isSuperuser"
                    v-for="level in (feature.required_access_levels || [])"
                    :key="level"
                    :level="level"
                    :label="accessLevelDisplay(level)"
                    class="ml-1"
                  />
                </td>
                <td
                  v-for="role in displayRoles"
                  :key="role.role_id"
                  :class="[
                    'px-4 py-1 text-sm text-gray-500 text-center',
                    isFeatureChanged(role.role_id, feature.id) ? 'bg-amber-100' : '',
                  ]"
                  style="line-height: 1em;"
                >
                  <input
                    type="checkbox"
                    :class="[
                      'h-4 w-4',
                      !canEditRoles || !isFeatureAvailableForRole(role.role_id, feature) ? 'pointer-events-none' : 'cursor-pointer',
                    ]"
                    :checked="isFeatureChecked(role.role_id, feature.id)"
                    :disabled="!canEditRoles || !isFeatureAvailableForRole(role.role_id, feature)"
                    :title="!isFeatureAvailableForRole(role.role_id, feature) ? 'Role lacks required access level' : feature.name"
                    @change="toggleFeature(role.role_id, feature.id)"
                  />
                </td>
              </tr>
              <!-- Children (nested features) -->
              <tr
                v-for="child in feature.children"
                v-show="expandedFeatures[feature.id]"
                :key="child.id"
                style="vertical-align: middle;"
              >
                <td
                  class="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 ps-7"
                  :title="featureTooltip(child)"
                >
                  {{ child.name }}
                  <span
                    v-if="child.is_beta"
                    class="ml-2 px-1.5 py-0.5 text-[10px] font-semibold rounded-full border border-purple-400 text-purple-600 normal-case tracking-normal leading-none inline-block"
                    title="Beta feature — hidden from non-superuser views"
                  >Beta</span>
                  <AccessLevelBadge
                    v-if="isSuperuser"
                    v-for="level in (child.required_access_levels || [])"
                    :key="level"
                    :level="level"
                    :label="accessLevelDisplay(level)"
                    class="ml-1"
                  />
                </td>
                <td
                  v-for="role in displayRoles"
                  :key="role.role_id"
                  :class="[
                    'px-4 py-1 text-sm text-gray-500 text-center',
                    isFeatureChanged(role.role_id, child.id) ? 'bg-amber-50' : '',
                  ]"
                  style="line-height: 1em;"
                >
                  <input
                    type="checkbox"
                    :class="[
                      'h-4 w-4',
                      !canEditRoles || !isFeatureAvailableForRole(role.role_id, child) ? 'pointer-events-none' : 'cursor-pointer',
                    ]"
                    :checked="isFeatureChecked(role.role_id, child.id)"
                    :disabled="!canEditRoles || !isFeatureAvailableForRole(role.role_id, child)"
                    :title="!isFeatureAvailableForRole(role.role_id, child) ? 'Role lacks required access level' : child.name"
                    @change="toggleFeature(role.role_id, child.id)"
                  />
                </td>
              </tr>
            </template>
          </tbody>
        </template>
      </table>
    </div>

    <!-- Add Feature Modal (superuser-only) -->
    <div
      v-if="showAddFeatureModal"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeAddFeatureModal"
    >
      <div class="bg-white rounded-lg p-6 w-[32rem] shadow-xl">
        <h3 class="text-lg font-semibold mb-4">Add Feature to Tenant</h3>
        <p class="text-sm text-gray-600 mb-4">
          Enable features for this tenant. Enabled features appear in the role
          matrix and can be granted to roles.
        </p>
        <input
          v-model="addFeatureSearch"
          type="text"
          class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md mb-3 focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
          placeholder="Search features..."
        />
        <div class="space-y-2 max-h-96 overflow-y-auto border border-gray-200 rounded p-3">
          <label
            v-for="feat in filteredAddableFeatures"
            :key="feat.id"
            class="flex items-start gap-2 text-sm cursor-pointer hover:bg-gray-50 p-2 rounded"
          >
            <input
              v-model="selectedAddFeatureIds"
              type="checkbox"
              :value="feat.id"
              class="mt-0.5"
            />
            <div class="flex-1">
              <div class="font-medium text-gray-900 flex items-center gap-1.5 flex-wrap">
                {{ feat.name }}
                <span
                  v-if="feat.is_beta"
                  class="px-1.5 py-0.5 text-[10px] font-semibold rounded-full border border-purple-400 text-purple-600"
                >Beta</span>
                <AccessLevelBadge
                  v-if="isSuperuser"
                  v-for="level in (feat.required_access_levels || [])"
                  :key="level"
                  :level="level"
                  :label="accessLevelDisplay(level)"
                  class="ml-1"
                />
                <span class="text-xs text-gray-400">({{ feat.category_name }})</span>
              </div>
              <div v-if="feat.description" class="text-xs text-gray-500 mt-0.5">
                {{ feat.description }}
              </div>
            </div>
          </label>
        </div>
        <p v-if="addFeatureError" class="text-red-600 text-sm mt-2">{{ addFeatureError }}</p>
        <div class="flex justify-end gap-2 mt-4">
          <button class="btn-secondary text-sm" @click="closeAddFeatureModal">Cancel</button>
          <button
            class="btn-primary text-sm"
            :disabled="selectedAddFeatureIds.length === 0 || addFeatureSaving"
            @click="submitAddFeature"
          >
            {{ addFeatureSaving ? 'Saving...' : `Add ${selectedAddFeatureIds.length || ''}` }}
          </button>
        </div>
      </div>
    </div>

    <!-- Role Modal (Add / Edit) -->
    <div
      v-if="roleModalMode"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="closeRoleModal"
    >
      <div class="bg-white rounded-lg p-6 w-96 shadow-xl">
        <h3 class="text-lg font-semibold mb-4">
          {{ roleModalMode === 'create' ? 'Create Role' : 'Edit Role' }}
        </h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Display Name</label>
            <input
              v-model="roleForm.display_name"
              type="text"
              class="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="e.g. Custom Role"
            />
          </div>
          <div v-if="isSuperuser">
            <label class="block text-sm font-medium text-gray-700 mb-2">API Access Levels</label>
            <p class="text-xs text-gray-500 mb-2">
              Controls which backend API endpoints this role can access.
            </p>
            <div class="space-y-2">
              <label
                v-for="level in accessLevels"
                :key="level.value"
                class="flex items-center gap-2 cursor-pointer"
              >
                <input
                  type="checkbox"
                  class="h-4 w-4"
                  :value="level.value"
                  :checked="roleForm.access_levels.includes(level.value)"
                  @change="toggleAccessLevel(level.value)"
                />
                <AccessLevelBadge :level="level.value" :label="level.label" />
              </label>
            </div>
            <p v-if="isEntityScopedRole" class="text-xs text-amber-600 mt-1">
              Portal and Restricted are entity-scoped and cannot be combined with other access levels.
            </p>
          </div>
          <p v-if="roleFormError" class="text-red-600 text-sm">{{ roleFormError }}</p>
        </div>
        <div class="flex justify-between items-center mt-6">
          <button
            v-if="roleModalMode === 'edit' && canCreateRole && canDeleteEditingRole"
            class="text-red-500 hover:text-red-700 text-sm transition-colors"
            @click="showDeleteConfirm = true"
          >
            <font-awesome-icon :icon="['fas', 'trash-can']" class="mr-1" />
            Delete
          </button>
          <span v-else></span>
          <div class="flex gap-2">
            <button class="btn-secondary text-sm" @click="closeRoleModal">Cancel</button>
            <button class="btn-primary text-sm" :disabled="roleFormSaving" @click="submitRoleForm">
              {{ roleFormSaving ? 'Saving...' : (roleModalMode === 'create' ? 'Create' : 'Save') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Role Confirmation -->
    <ConfirmationModal
      :is-visible="showDeleteConfirm"
      :message="`Are you sure you want to delete '${editingRole?.display_name}'? This will remove all feature mappings for this role${editingRole?.user_count ? ` and impact ${editingRole.user_count} user${editingRole.user_count === 1 ? '' : 's'}` : ''}.`"
      @confirm="deleteRole"
      @cancel="showDeleteConfirm = false"
    />

    <!-- Save Confirmation Modal -->
    <div
      v-if="showSaveConfirm && saveConfirmSummary"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="cancelSaveConfirm"
    >
      <div class="bg-white rounded-lg p-6 w-[480px] shadow-xl max-h-[80vh] flex flex-col">
        <h3 class="text-lg font-semibold mb-4">Confirm Changes</h3>
        <div class="overflow-y-auto flex-1 space-y-3">
          <div
            v-for="rc in saveConfirmSummary.roleChanges"
            :key="rc.roleId"
            :class="(rc.newAccessLevels.length || rc.removedAccessLevels.length) ? 'border-amber-300 bg-amber-50' : 'border-gray-200'"
            class="border rounded-lg px-4 py-3"
          >
            <p class="text-md font-semibold text-gray-800">
              {{ rc.roleName }}
              <span class="font-normal text-gray-500">&mdash; {{ rc.userCount }} {{ rc.userCount === 1 ? 'user' : 'users' }}</span>
            </p>
            <div v-if="rc.newAccessLevels.length || rc.removedAccessLevels.length" class="mt-2">
              <p class="text-sm font-semibold text-amber-700 mb-1">
                <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="mr-1" />Access Levels
              </p>
              <p v-if="rc.newAccessLevels.length" class="text-sm text-gray-600">
                <span class="text-green-700 font-semibold">Add:</span>
                <AccessLevelBadge
                  v-for="level in rc.newAccessLevels"
                  :key="level"
                  :level="level"
                  :label="accessLevelDisplay(level)"
                  class="ml-1"
                />
              </p>
              <p v-if="rc.removedAccessLevels.length" class="text-sm text-gray-600">
                <span class="text-red-600 font-semibold">Remove:</span>
                <AccessLevelBadge
                  v-for="level in rc.removedAccessLevels"
                  :key="'rm-' + level"
                  :level="level"
                  :label="accessLevelDisplay(level)"
                  class="ml-1"
                />
              </p>
            </div>
            <hr v-if="(rc.newAccessLevels.length || rc.removedAccessLevels.length) && (rc.added.length || rc.removed.length)" class="my-2 border-amber-300" />
            <div v-if="rc.added.length || rc.removed.length" class="mt-2">
              <p class="text-sm font-semibold text-gray-500 mb-1">Features</p>
              <p v-if="rc.added.length" class="text-sm text-gray-600">
                <span class="text-green-700 font-semibold">Add:</span> {{ rc.added.join(', ') }}
              </p>
              <p v-if="rc.removed.length" class="text-sm text-gray-600">
                <span class="text-red-600 font-semibold">Remove:</span> {{ rc.removed.join(', ') }}
              </p>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button class="btn-secondary text-sm" @click="cancelSaveConfirm">Cancel</button>
          <button class="btn-primary text-sm" :disabled="saving" @click="confirmSave">
            {{ saving ? 'Saving...' : 'Confirm & Save' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { canAccessFeature } from '@/utils/access';
import { FEATURES } from '@/constants/features';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import AccessLevelBadge from '@/components/widgetcomponents/library/AccessLevelBadge.vue';
import { exportToExcel } from '@/utils/exportArraytoXlsx.js';
import ConfirmationModal from '@/components/widgetcomponents/ConfirmationModal.vue';
import { RESERVED_ROLE_IDS } from '@/constants/roles';

export default {
  name: 'ConfigRoles',
  components: {
    ComponentHeader,
    ExportMenu,
    AccessLevelBadge,
    ConfirmationModal,
    FontAwesomeIcon,
  },
  data() {
    return {
      localRoleFeatures: {},
      saving: false,
      expandedFeatures: {},
      roleModalMode: null,
      editingRole: null,
      roleForm: { display_name: '', access_levels: [] },
      roleFormError: '',
      roleFormSaving: false,
      showDeleteConfirm: false,
      showAddFeatureModal: false,
      selectedAddFeatureIds: [],
      addFeatureError: '',
      addFeatureSaving: false,
      featureSearch: '',
      addFeatureSearch: '',
      showSaveConfirm: false,
      saveConfirmSummary: null,
      pendingRoleSubmit: null,
      localRoleFeaturesSnapshot: null,
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    ...mapGetters('auth', ['current_user']),
    // `roles` is the full list including hide_from_config — needed by
    // designationTaken() so a legacy hidden role still counts as holding
    // its designation. `visibleRoles` is the subset rendered in the matrix.
    ...mapGetters('featureConfig', ['features', 'roles', 'roleFeatures', 'roleAccessLevels', 'accessLevels', 'addableFeatures', 'visibleRoles']),
    displayRoles() {
      // Role columns rendered in the matrix — hidden roles (hide_from_config)
      // are stored for legacy assignment but shouldn't appear for editing.
      return this.visibleRoles;
    },
    groupedFeatures() {
      const q = this.featureSearch.toLowerCase().trim();
      if (!q) return this.features;
      return this.features
        .map((group) => ({
          ...group,
          features: group.features.filter(
            (f) =>
              f.name.toLowerCase().includes(q) ||
              (f.children || []).some((c) => c.name.toLowerCase().includes(q)),
          ),
        }))
        .filter((group) => group.features.length > 0);
    },
    exportRoles() {
      // Excel export uses the same visible set but in { id, name } shape.
      return this.visibleRoles.map(r => ({ id: r.role_id, name: r.display_name }));
    },
    featureMap() {
      const map = {};
      for (const group of this.features) {
        for (const feature of group.features) {
          map[feature.id] = feature;
          for (const child of feature.children || []) {
            map[child.id] = child;
          }
        }
      }
      return map;
    },
    isSuperuser() {
      return !!this.current_user?.is_superuser;
    },
    canEditRoles() {
      return canAccessFeature(FEATURES.ACTION_ROLE_CONFIG);
    },
    canCreateRole() {
      if (this.isSuperuser) return true;
      const userRoles = this.$store.getters['tenant/current_tenant_roles'] || [];
      return userRoles.some(
        (role) => (this.roleAccessLevels[role] || []).includes('security'),
      );
    },
    canAddFeature() {
      // Enabling new features for a tenant is superuser-only.
      return this.isSuperuser;
    },
    canDeleteEditingRole() {
      return this.editingRole && !RESERVED_ROLE_IDS.includes(this.editingRole.role_id);
    },
    filteredAddableFeatures() {
      const q = this.addFeatureSearch.toLowerCase().trim();
      if (!q) return this.addableFeatures;
      return this.addableFeatures.filter(
        (f) => f.name.toLowerCase().includes(q) || (f.description || '').toLowerCase().includes(q),
      );
    },
    isDirty() {
      return JSON.stringify(this.localRoleFeatures) !== JSON.stringify(this.roleFeatures);
    },
    isEntityScopedRole() {
      return this.roleForm.access_levels.some((l) => l === 'portal' || l === 'restricted');
    },
  },
  watch: {
    roleFeatures: {
      handler(val) {
        this.localRoleFeatures = JSON.parse(JSON.stringify(val));
      },
      immediate: true,
      deep: true,
    },
    current_tenant_slug() {
      // Tenant switch while on this view → refresh the config for the new tenant.
      this.$store.dispatch('featureConfig/fetchConfig');
    },
  },
  mounted() {
    this.$store.dispatch('featureConfig/fetchConfig');
  },
  methods: {
    accessLevelDisplay(level) {
      const found = this.accessLevels.find((al) => al.value === level);
      return found ? found.label : level;
    },
    toggleAccessLevel(value) {
      const entityScoped = ['portal', 'restricted'];
      const current = this.roleForm.access_levels;
      if (current.includes(value)) {
        this.roleForm.access_levels = current.filter((l) => l !== value);
      } else if (entityScoped.includes(value)) {
        this.roleForm.access_levels = current.filter((l) => entityScoped.includes(l)).concat(value);
      } else {
        this.roleForm.access_levels = current.filter((l) => !entityScoped.includes(l)).concat(value);
      }
    },
    exportToExcel() {
      const roles = this.exportRoles;
      const rows = [];
      for (const group of this.groupedFeatures) {
        for (const feature of group.features) {
          const row = { Category: group.category_name, Feature: feature.name };
          for (const role of roles) {
            row[role.name] = this.roleFeatures[role.id]?.includes(feature.id) ? 'Yes' : '';
          }
          rows.push(row);
        }
      }
      const columnWidths = { Category: 160, Feature: 200 };
      for (const role of roles) {
        columnWidths[role.name] = 120;
      }
      const today = new Date().toISOString().split('T')[0];
      exportToExcel(rows, `role_configurations_${today}.xlsx`, {
        sheetName: 'Role Configurations',
        columnWidths,
      });
    },
    toggleExpanded(featureId) {
      this.expandedFeatures = { ...this.expandedFeatures, [featureId]: !this.expandedFeatures[featureId] };
    },
    discardChanges() {
      this.localRoleFeatures = JSON.parse(JSON.stringify(this.roleFeatures));
    },
    isFeatureChanged(roleId, featureId) {
      const savedFeats = this.roleFeatures[roleId] || [];
      const localFeats = this.localRoleFeatures[roleId] || [];
      return savedFeats.includes(featureId) !== localFeats.includes(featureId);
    },
    isFeatureChecked(roleId, featureId) {
      const roleFeats = this.localRoleFeatures[roleId] || [];
      return roleFeats.includes(featureId);
    },
    featureTooltip(feature) {
      const parts = [feature.description || feature.name];
      const parent = feature.required_feature && this.featureMap[feature.required_feature];
      if (parent) parts.push(`Requires feature: ${parent.name}`);
      return parts.join('\n');
    },
    isFeatureAvailableForRole(roleId, feature) {
      const required = feature.required_access_levels || [];
      if (!required.length) return true;
      const roleLevels = this.roleAccessLevels[roleId] || [];
      return required.some((level) => roleLevels.includes(level));
    },
    toggleFeature(roleId, featureId) {
      if (!this.localRoleFeatures[roleId]) {
        this.localRoleFeatures[roleId] = [];
      }
      const arr = this.localRoleFeatures[roleId];
      const idx = arr.indexOf(featureId);
      if (idx >= 0) {
        arr.splice(idx, 1);
        this.cascadeRevokeDependents(arr, featureId);
      } else {
        arr.push(featureId);
        this.cascadeGrantParents(arr, featureId);
      }
    },
    saveChanges() {
      this.saveConfirmSummary = this.computeChangeSummary();
      this.showSaveConfirm = true;
    },
    computeChangeSummary() {
      const roleChanges = [];
      for (const role of this.displayRoles) {
        const saved = this.roleFeatures[role.role_id] || [];
        const local = this.localRoleFeatures[role.role_id] || [];
        const added = local
          .filter((f) => !saved.includes(f))
          .map((id) => this.featureMap[id]?.name || id);
        const removed = saved
          .filter((f) => !local.includes(f))
          .map((id) => this.featureMap[id]?.name || id);
        const hasPendingLevelChange = this.pendingRoleSubmit?.roleId === role.role_id;
        if (!added.length && !removed.length && !hasPendingLevelChange) continue;

        const newAccessLevels = [];
        const removedAccessLevels = [];
        if (this.pendingRoleSubmit && role.role_id === this.pendingRoleSubmit.roleId) {
          const prev = this.pendingRoleSubmit.previousLevels || [];
          const next = this.pendingRoleSubmit.accessLevels;
          for (const l of next) {
            if (!prev.includes(l)) newAccessLevels.push(l);
          }
          for (const l of prev) {
            if (!next.includes(l)) removedAccessLevels.push(l);
          }
        }

        roleChanges.push({
          roleId: role.role_id,
          roleName: role.display_name,
          userCount: role.user_count || 0,
          added,
          removed,
          newAccessLevels,
          removedAccessLevels,
        });
      }

      return {
        roleChanges,
        totalFeaturesAdded: roleChanges.reduce((s, r) => s + r.added.length, 0),
        totalFeaturesRemoved: roleChanges.reduce((s, r) => s + r.removed.length, 0),
        totalAccessLevelChanges: roleChanges.reduce((s, r) => s + r.newAccessLevels.length + r.removedAccessLevels.length, 0),
        totalUsersImpacted: roleChanges.reduce((s, r) => s + r.userCount, 0),
      };
    },
    async confirmSave() {
      this.saving = true;
      try {
        if (this.pendingRoleSubmit) {
          const { roleId, displayName, accessLevels } = this.pendingRoleSubmit;
          const payload = { display_name: displayName };
          if (this.isSuperuser) {
            payload.access_levels = accessLevels;
          }
          await axios.patch(`/api/features-config/roles/${roleId}/update/`, payload);
        }
        await axios.put('/api/features-config/role-features/', {
          role_features: this.localRoleFeatures,
        });
        await this.$store.dispatch('featureConfig/fetchConfig');
        this.pendingRoleSubmit = null;
        this.localRoleFeaturesSnapshot = null;
        this.showSaveConfirm = false;
      } catch (error) {
        console.error('Failed to save:', error);
      } finally {
        this.saving = false;
      }
    },
    cancelSaveConfirm() {
      if (this.pendingRoleSubmit) {
        if (this.localRoleFeaturesSnapshot) {
          this.localRoleFeatures = this.localRoleFeaturesSnapshot;
        }
        const role = this.displayRoles.find(
          (r) => r.role_id === this.pendingRoleSubmit.roleId,
        );
        if (role) {
          this.roleModalMode = 'edit';
          this.editingRole = role;
          this.roleFormError = '';
          this.showDeleteConfirm = false;
          this.roleForm = {
            display_name: this.pendingRoleSubmit.displayName,
            access_levels: [...this.pendingRoleSubmit.accessLevels],
          };
        }
        this.pendingRoleSubmit = null;
        this.localRoleFeaturesSnapshot = null;
      }
      this.showSaveConfirm = false;
    },
    async deleteRole() {
      try {
        await axios.delete(`/api/features-config/roles/${this.editingRole.role_id}/`);
        await this.$store.dispatch('featureConfig/fetchConfig');
        this.showDeleteConfirm = false;
        this.closeRoleModal();
      } catch (error) {
        this.showDeleteConfirm = false;
        const data = error.response?.data;
        this.roleFormError =
          data?.error || data?.detail || error.message || 'Failed to delete role.';
      }
    },
    openCreateRoleModal() {
      this.roleModalMode = 'create';
      this.editingRole = null;
      this.roleFormError = '';
      this.showDeleteConfirm = false;
      this.roleForm = { display_name: '', access_levels: ['standard'] };
    },
    openAddFeatureModal() {
      this.selectedAddFeatureIds = [];
      this.addFeatureError = '';
      this.addFeatureSearch = '';
      this.showAddFeatureModal = true;
    },
    closeAddFeatureModal() {
      this.showAddFeatureModal = false;
      this.selectedAddFeatureIds = [];
      this.addFeatureError = '';
      this.addFeatureSearch = '';
    },
    async submitAddFeature() {
      if (this.selectedAddFeatureIds.length === 0) return;
      this.addFeatureSaving = true;
      this.addFeatureError = '';
      try {
        await axios.post('/api/features-config/features/', {
          feature_ids: this.selectedAddFeatureIds,
        });
        await this.$store.dispatch('featureConfig/fetchConfig');
        this.closeAddFeatureModal();
      } catch (error) {
        const data = error.response?.data;
        this.addFeatureError = data?.error || data?.detail || error.message || 'Failed to add features.';
      } finally {
        this.addFeatureSaving = false;
      }
    },
    openEditRoleModal(role) {
      this.roleModalMode = 'edit';
      this.editingRole = role;
      this.roleFormError = '';
      this.showDeleteConfirm = false;
      this.roleForm = {
        display_name: role.display_name,
        access_levels: [...(this.roleAccessLevels[role.role_id] || [])],
      };
    },
    closeRoleModal() {
      this.roleModalMode = null;
      this.editingRole = null;
      this.roleFormError = '';
      this.showDeleteConfirm = false;
    },
    async submitRoleForm() {
      this.roleFormError = '';
      const displayName = this.roleForm.display_name.trim();
      if (!displayName) {
        this.roleFormError = 'Display Name is required.';
        return;
      }
      this.roleFormSaving = true;
      try {
        let roleId;
        if (this.roleModalMode === 'create') {
          const payload = { display_name: displayName, access_levels: this.roleForm.access_levels };
          const response = await axios.post('/api/features-config/roles/', payload);
          roleId = response.data.role_id;
          await this.$store.dispatch('featureConfig/fetchConfig');
        } else {
          roleId = this.editingRole.role_id;
        }

        const previousLevels = this.roleModalMode === 'edit'
          ? (this.roleAccessLevels[roleId] || [])
          : [];
        const newLevels = this.roleForm.access_levels.filter((l) => !previousLevels.includes(l));
        const removedLevels = previousLevels.filter((l) => !this.roleForm.access_levels.includes(l));

        if (!newLevels.length && !removedLevels.length) {
          if (this.roleModalMode === 'edit') {
            const payload = { display_name: displayName };
            if (this.isSuperuser) {
              payload.access_levels = this.roleForm.access_levels;
            }
            await axios.patch(`/api/features-config/roles/${roleId}/update/`, payload);
            await this.$store.dispatch('featureConfig/fetchConfig');
          }
          this.closeRoleModal();
          return;
        }

        this.localRoleFeaturesSnapshot = JSON.parse(JSON.stringify(this.localRoleFeatures));
        if (newLevels.length > 0) {
          this.autoGrantFeaturesForLevels(roleId, newLevels);
        }
        if (removedLevels.length > 0) {
          this.autoRevokeFeaturesForLevels(roleId, removedLevels);
        }
        this.pendingRoleSubmit = {
          roleId,
          displayName,
          accessLevels: [...this.roleForm.access_levels],
          previousLevels,
        };
        this.closeRoleModal();
        this.saveConfirmSummary = this.computeChangeSummary();
        this.showSaveConfirm = true;
      } catch (error) {
        this.roleFormError = error.response?.data?.error || 'Failed to save role.';
      } finally {
        this.roleFormSaving = false;
      }
    },
    autoGrantFeaturesForLevels(roleId, levels) {
      if (!this.localRoleFeatures[roleId]) {
        this.localRoleFeatures[roleId] = [];
      }
      const arr = this.localRoleFeatures[roleId];
      for (const group of this.features) {
        for (const feature of group.features) {
          if ((feature.required_access_levels || []).some((l) => levels.includes(l))) {
            if (!arr.includes(feature.id)) arr.push(feature.id);
            this.cascadeGrantParents(arr, feature.id);
          }
          for (const child of feature.children || []) {
            if ((child.required_access_levels || []).some((l) => levels.includes(l))) {
              if (!arr.includes(child.id)) arr.push(child.id);
              this.cascadeGrantParents(arr, child.id);
            }
          }
        }
      }
    },
    autoRevokeFeaturesForLevels(roleId, removedLevels) {
      const arr = this.localRoleFeatures[roleId];
      if (!arr) return;
      const remainingLevels = this.roleForm.access_levels;
      const toRevoke = [];
      for (const group of this.features) {
        for (const feature of [...group.features, ...(group.features.flatMap((f) => f.children || []))]) {
          const required = feature.required_access_levels || [];
          if (required.length === 0) continue;
          const needsRemovedLevel = required.some((l) => removedLevels.includes(l));
          const coveredByRemaining = required.some((l) => remainingLevels.includes(l));
          if (needsRemovedLevel && !coveredByRemaining) {
            toRevoke.push(feature.id);
          }
        }
      }
      for (const fid of toRevoke) {
        const idx = arr.indexOf(fid);
        if (idx >= 0) arr.splice(idx, 1);
        this.cascadeRevokeDependents(arr, fid);
      }
    },
    cascadeGrantParents(arr, featureId) {
      const feat = this.featureMap[featureId];
      if (!feat?.required_feature) return;
      const parentId = feat.required_feature;
      if (!arr.includes(parentId)) {
        arr.push(parentId);
        this.cascadeGrantParents(arr, parentId);
      }
    },
    cascadeRevokeDependents(arr, featureId) {
      for (const fid in this.featureMap) {
        if (this.featureMap[fid].required_feature === featureId && arr.includes(fid)) {
          const idx = arr.indexOf(fid);
          if (idx >= 0) arr.splice(idx, 1);
          this.cascadeRevokeDependents(arr, fid);
        }
      }
    },
  },
};
</script>
